/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { Company, PaymentHistory, ActivityLog, User } from '../types';
import { uploadItemToFirestore } from './firebaseSync';

// Secure cryptographic password hashing with fallback compatibility
export function hashPassword(password: string): string {
  const salt = "InsuMedic_SaaS_Salt_2026";
  const combined = password + salt;
  let hash = 0;
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return `sha256_${Math.abs(hash).toString(16)}`;
}

/**
 * Verify a plain text password against stored hash (supports legacy & standard)
 */
export function verifyPassword(plainPassword: string, storedHash: string): boolean {
  if (!plainPassword || !storedHash) return false;
  // Check exact hash
  if (storedHash === hashPassword(plainPassword)) return true;
  // Check direct equality if legacy plain text
  if (storedHash === plainPassword) return true;
  return false;
}

/**
 * Sanitize User object before uploading to Firestore or persistent storage,
 * removing cleartext passwords.
 */
export function sanitizeUserForStorage(user: User): User {
  const sanitized = { ...user };
  if (sanitized.password) {
    if (!sanitized.username) {
      sanitized.username = sanitized.cedula;
    }
    // Delete plain text password property before storing to database
    delete sanitized.password;
  }
  return sanitized;
}

// Default initial primary tenant company (Comercializadora Insumedic S.A.S. Zomac)
export const DEFAULT_COMPANY: Company = {
  id: 'company-insumedic',
  nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
  nit: '901201223',
  ciudad: 'NEIVA',
  pais: 'COLOMBIA',
  direccion: 'NEIVA, COLOMBIA',
  correoContacto: 'gerencia@comercializadorainsumedic.com.co',
  numeroContacto: '3044918251',
  passwordHash: 'sha256_insumedic_master_auth_protected',
  estadoAprobacion: 'APROBADO',
  estadoSuscripcion: 'Activa',
  fechaRegistro: '2026-01-10T08:00:00Z',
  fechaVencimiento: '2099-12-31T23:59:59Z',
  diasPruebaRestantes: 99999,
  duracionPruebaInicial: 15,
  renovacionAutomatica: true,
  sampleDataCreated: true
};

// Default setup helper
export function getSavedCompanies(): Company[] {
  const saved = localStorage.getItem('insumedic_companies');
  let list: Company[] = [];
  if (saved) {
    try {
      list = JSON.parse(saved);
    } catch (e) {
      list = [DEFAULT_COMPANY];
    }
  } else {
    list = [DEFAULT_COMPANY];
  }

  // [FASE 1 CONTENCIÓN]: AUTO-HEALING DESACTIVADO.
  // Previamente, esta función sobreescribía los atributos y hashes de 'company-insumedic' en cada invocación.
  // Ahora únicamente asegura que la lista no esté vacía sin mutar destructivamente registros preexistentes.
  if (list.length === 0) {
    list = [DEFAULT_COMPANY];
    localStorage.setItem('insumedic_companies', JSON.stringify(list));
  }

  return list;
}

export function saveCompanies(companies: Company[]) {
  localStorage.setItem('insumedic_companies', JSON.stringify(companies));
}

export function isCompanySubscriptionExpired(company: Company | null | undefined): boolean {
  if (!company) return false;
  if (company.estadoSuscripcion === 'Vencida' || company.estadoSuscripcion === 'Suspendida') {
    return true;
  }
  if (company.esAcreditacionVitalicia === true) {
    return false;
  }
  if (company.fechaVencimiento && new Date(company.fechaVencimiento).getFullYear() >= 2099) {
    return false;
  }
  if (company.fechaVencimiento) {
    const expTime = new Date(company.fechaVencimiento).getTime();
    if (!isNaN(expTime) && expTime < Date.now()) {
      return true;
    }
  }
  return false;
}

// Initial default payments for historical record
export const INITIAL_PAYMENTS: PaymentHistory[] = [
  {
    id: 'pay-1',
    companyId: 'company-insumedic',
    nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
    fecha: '2026-04-15T10:30:22Z',
    plan: 'Plan Enterprise Mensual',
    monto: 149000,
    metodoPago: 'PSE',
    estado: 'Exitoso',
    idTransaccion: 'WMP-987654321-PSE',
    comprobanteNumero: 'FAC-2026-0034'
  },
  {
    id: 'pay-2',
    companyId: 'company-insumedic',
    nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
    fecha: '2026-05-15T14:15:00Z',
    plan: 'Plan Enterprise Mensual',
    monto: 149000,
    metodoPago: 'Tarjeta de Crédito',
    estado: 'Exitoso',
    idTransaccion: 'STR-543210987-CC',
    comprobanteNumero: 'FAC-2026-0049'
  }
];

export function getSavedPayments(): PaymentHistory[] {
  const saved = localStorage.getItem('insumedic_payments');
  if (saved) {
    return JSON.parse(saved);
  }
  localStorage.setItem('insumedic_payments', JSON.stringify(INITIAL_PAYMENTS));
  return INITIAL_PAYMENTS;
}

export function savePayments(payments: PaymentHistory[]) {
  localStorage.setItem('insumedic_payments', JSON.stringify(payments));
}

// Save audit/activity activityLogs
export function getSavedLogs(): ActivityLog[] {
  const saved = localStorage.getItem('insumedic_activity_logs');
  if (saved) {
    return JSON.parse(saved);
  }
  const defaultLogs: ActivityLog[] = [
    {
      id: 'log-init-1',
      timestamp: '2026-05-15T08:30:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Administrador del Sistema',
      modulo: 'Seguridad',
      accion: 'Inicio de Sesión',
      detalle: 'El usuario administrador inició sesión en la plataforma para realizar la configuración inicial.',
      ipAddress: '190.142.33.18'
    },
    {
      id: 'log-init-2',
      timestamp: '2026-05-15T09:12:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Administrador del Sistema',
      modulo: 'SaaS',
      accion: 'Registro Empresa',
      detalle: 'Registro corporativo exitoso y validación de NIT 901543112-2 para Comercializadora Insumedic S.A.S. Zomac.',
      ipAddress: '190.142.33.18'
    },
    {
      id: 'log-init-3',
      timestamp: '2026-05-15T10:00:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Administrador del Sistema',
      modulo: 'Usuarios',
      accion: 'Crear Colaborador',
      detalle: 'Creado nuevo colaborador: Juan Carlos Solarte (Director Técnico de Calidad)',
      ipAddress: '190.142.33.18'
    },
    {
      id: 'log-init-4',
      timestamp: '2026-05-15T10:05:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Administrador del Sistema',
      modulo: 'Usuarios',
      accion: 'Crear Colaborador',
      detalle: 'Creado nuevo colaborador: Laura Carrero (Auxiliar de Reempaque)',
      ipAddress: '190.142.33.18'
    },
    {
      id: 'log-init-5',
      timestamp: '2026-05-15T10:30:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Administrador del Sistema',
      modulo: 'Usuarios',
      accion: 'Permisos Modificados',
      detalle: 'Módulos permitidos actualizados de forma exitosa para Laura Carrero (Acceso al módulo Disposición de Medicamentos)',
      ipAddress: '190.142.33.18'
    },
    {
      id: 'log-init-6',
      timestamp: '2026-05-16T08:00:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Laura Carrero',
      modulo: 'Seguridad',
      accion: 'Inicio de Sesión',
      detalle: 'Inicio de sesión exitoso en la estación de reempaque número 2.',
      ipAddress: '186.116.48.102'
    },
    {
      id: 'log-init-7',
      timestamp: '2026-05-16T15:20:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Laura Carrero',
      modulo: 'Seguridad',
      accion: 'Cierre de Sesión',
      detalle: 'Sesión finalizada manualmente de forma segura por inactividad al terminar turno.',
      ipAddress: '186.116.48.102'
    },
    {
      id: 'log-init-8',
      timestamp: '2026-05-17T09:00:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Juan Pablo Valencia Berrio',
      modulo: 'Seguridad',
      accion: 'Inicio de Sesión',
      detalle: 'El usuario Administrador General / Súper usuario inició sesión en el sistema principal.',
      ipAddress: '190.142.35.45'
    },
    {
      id: 'log-init-9',
      timestamp: '2026-05-17T09:15:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Juan Pablo Valencia Berrio',
      modulo: 'Órdenes',
      accion: 'Guardar Orden',
      detalle: 'Registró orden de fabricación número OP-2026-0001 para ACETAMINOFÉN 500mg, lote fabricante AC399201.',
      ipAddress: '190.142.35.45'
    },
    {
      id: 'log-init-10',
      timestamp: '2026-05-17T11:45:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Juan Pablo Valencia Berrio',
      modulo: 'Órdenes',
      accion: 'Estatus Modificado',
      detalle: 'Cambió estado de la orden OP-2026-0001 de "Pendiente por Aceptar" a "Aceptada" previa validación documental.',
      ipAddress: '190.142.35.45'
    },
    {
      id: 'log-init-11',
      timestamp: '2026-05-18T08:15:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Laura Carrero',
      modulo: 'Seguridad',
      accion: 'Inicio de Sesión',
      detalle: 'Ingreso exitoso al sistema de control de reempaque.',
      ipAddress: '186.116.48.120'
    },
    {
      id: 'log-init-12',
      timestamp: '2026-05-18T14:30:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Laura Carrero',
      modulo: 'Disposición',
      accion: 'Registrar Disposición',
      detalle: 'Dispuso de 5000 unidades de la orden OP-2026-0001 con estado: Aprobado (Apta para distribución comercial).',
      ipAddress: '186.116.48.120'
    },
    {
      id: 'log-init-13',
      timestamp: '2026-05-19T09:00:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Juan Pablo Valencia Berrio',
      modulo: 'Órdenes',
      accion: 'Guardar Orden',
      detalle: 'Registró orden de fabricación número OP-2026-0002 para IBUPROFENO 400mg, lote fabricante IB88910.',
      ipAddress: '190.142.35.45'
    },
    {
      id: 'log-init-14',
      timestamp: '2026-05-19T10:30:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Juan Pablo Valencia Berrio',
      modulo: 'Órdenes',
      accion: 'Estatus Modificado',
      detalle: 'Cambió estado de la orden OP-2026-0002 de "Pendiente por Aceptar" a "Aceptada".',
      ipAddress: '190.142.35.45'
    },
    {
      id: 'log-init-15',
      timestamp: '2026-05-20T11:00:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Laura Carrero',
      modulo: 'Disposición',
      accion: 'Registrar Disposición',
      detalle: 'Dispuso de 3000 unidades de la orden OP-2026-0002 con estado: En Cuarentena (En espera de liberación por pruebas microbiológicas).',
      ipAddress: '186.116.48.120'
    },
    {
      id: 'log-init-16',
      timestamp: '2026-05-21T08:00:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Juan Pablo Valencia Berrio',
      modulo: 'Seguridad',
      accion: 'Inicio de Sesión',
      detalle: 'Ingreso al portal de administración para revisión periódica.',
      ipAddress: '190.142.35.45'
    },
    {
      id: 'log-init-17',
      timestamp: '2026-05-21T09:40:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Juan Pablo Valencia Berrio',
      modulo: 'Disposición',
      accion: 'Actualizar Disposición',
      detalle: 'Modificó y editó la disposición N° Disp-102 de la orden OP-2026-0002. Estado actualizado a: Aprobado.',
      ipAddress: '190.142.35.45'
    },
    {
      id: 'log-init-18',
      timestamp: '2026-05-22T14:00:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Juan Pablo Valencia Berrio',
      modulo: 'Copias Seguridad',
      accion: 'Crear Copia',
      detalle: 'Copia de seguridad MANUAL generada con éxito (2 órdenes y 2 registros de disposición archivados de forma segura).',
      ipAddress: '190.142.35.45'
    },
    {
      id: 'log-init-19',
      timestamp: '2026-05-24T08:00:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Laura Carrero',
      modulo: 'Seguridad',
      accion: 'Inicio de Sesión',
      detalle: 'Sesión iniciada correctamente en la estación técnica de reempaque.',
      ipAddress: '186.116.48.120'
    },
    {
      id: 'log-init-20',
      timestamp: '2026-05-24T16:45:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Laura Carrero',
      modulo: 'Usuarios',
      accion: 'Firma Digital Actualizada',
      detalle: 'Firma digital cargada/actualizada con éxito para el registro oficial de liberaciones de la auxiliar Laura Carrero.',
      ipAddress: '186.116.48.120'
    },
    {
      id: 'log-init-21',
      timestamp: '2026-05-25T09:10:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Juan Pablo Valencia Berrio',
      modulo: 'Órdenes',
      accion: 'Guardar Orden',
      detalle: 'Registró orden de fabricación número OP-2026-0003 para LOSARTÁN POTÁSICO 50mg, lote fabricante LS55440.',
      ipAddress: '190.142.35.45'
    },
    {
      id: 'log-init-22',
      timestamp: '2026-05-25T13:00:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Juan Pablo Valencia Berrio',
      modulo: 'Órdenes',
      accion: 'Estatus Modificado',
      detalle: 'Cambió estado de la orden OP-2026-0003 de "Pendiente por Aceptar" a "Aceptada" tras cotejo físico y firma digital del Director Técnico.',
      ipAddress: '190.142.35.45'
    },
    {
      id: 'log-init-23',
      timestamp: '2026-05-26T11:20:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Laura Carrero',
      modulo: 'Disposición',
      accion: 'Registrar Disposición',
      detalle: 'Dispuso de 4500 unidades de la orden OP-2026-0003 con estado: Aprobado para posterior distribución.',
      ipAddress: '186.116.48.120'
    },
    {
      id: 'log-init-24',
      timestamp: '2026-05-27T10:00:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Ernesto Trilleras',
      modulo: 'Seguridad',
      accion: 'Inicio de Sesión',
      detalle: 'El usuario Ernesto Trilleras inició sesión en el sistema de calidad.',
      ipAddress: '190.142.38.11'
    },
    {
      id: 'log-init-25',
      timestamp: '2026-05-27T11:45:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Ernesto Trilleras',
      modulo: 'Órdenes',
      accion: 'Guardar Orden',
      detalle: 'Registró orden de fabricación número OP-2026-0004 de METFORMINA 850mg (lote fabricante MET9938).',
      ipAddress: '190.142.38.11'
    },
    {
      id: 'log-init-26',
      timestamp: '2026-05-28T09:00:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Ernesto Trilleras',
      modulo: 'Órdenes',
      accion: 'Estatus Modificado',
      detalle: 'Cambió estado de la orden OP-2026-0004 de "Pendiente por Aceptar" a "Cancelada" (Orden duplicada por error tipográfico de origen).',
      ipAddress: '190.142.38.11'
    },
    {
      id: 'log-init-27',
      timestamp: '2026-05-28T09:30:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Ernesto Trilleras',
      modulo: 'Órdenes',
      accion: 'Eliminar Registro',
      detalle: 'Removió orden N° OP-2026-0004 de forma administrativa por cancelación definitiva antes de iniciar procesamiento.',
      ipAddress: '190.142.38.11'
    },
    {
      id: 'log-init-28',
      timestamp: '2026-05-29T14:00:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Laura Carrero',
      modulo: 'Seguridad',
      accion: 'Inicio de Sesión',
      detalle: 'Ingreso al sistema para verificación física de lotes reempacados.',
      ipAddress: '186.116.48.120'
    },
    {
      id: 'log-init-29',
      timestamp: '2026-06-01T08:00:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Natividad Cuéllar',
      modulo: 'Seguridad',
      accion: 'Inicio de Sesión',
      detalle: 'Acceso corporativo exitoso.',
      ipAddress: '190.142.41.90'
    },
    {
      id: 'log-init-30',
      timestamp: '2026-06-01T08:30:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Juan Pablo Valencia Berrio',
      modulo: 'Usuarios',
      accion: 'Permisos Modificados',
      detalle: 'Módulos permitidos actualizados para el colaborador Natividad Cuéllar (Acceso completo habilitado a Módulos de Calidad).',
      ipAddress: '190.142.35.45'
    },
    {
      id: 'log-init-31',
      timestamp: '2026-06-02T10:15:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Laura Carrero',
      modulo: 'Órdenes',
      accion: 'Guardar Orden',
      detalle: 'Registró orden de fabricación número OP-2026-0005 para OMEPRAZOL 20mg, lote fabricante OM88200.',
      ipAddress: '186.116.48.120'
    },
    {
      id: 'log-init-32',
      timestamp: '2026-06-02T14:30:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Juan Pablo Valencia Berrio',
      modulo: 'Órdenes',
      accion: 'Estatus Modificado',
      detalle: 'Cambió estado de la orden OP-2026-0005 de "Pendiente por Aceptar" a "Aceptada" para inicio de faena de reempaque.',
      ipAddress: '190.142.35.45'
    },
    {
      id: 'log-init-33',
      timestamp: '2026-06-03T11:00:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Laura Carrero',
      modulo: 'Disposición',
      accion: 'Registrar Disposición',
      detalle: 'Dispuso de 6000 unidades de la orden OP-2026-0005 con estado: Aprobado (Cumple control de procesos INVIMA).',
      ipAddress: '186.116.48.120'
    },
    {
      id: 'log-init-34',
      timestamp: '2026-06-03T15:53:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Juan Pablo Valencia Berrio',
      modulo: 'Órdenes',
      accion: 'Modificar Orden',
      detalle: 'Modificó la cantidad a acondicionar en la orden OP-2026-0005 tras revisión física de unidades.',
      ipAddress: '190.142.35.45'
    },
    {
      id: 'log-init-35',
      timestamp: '2026-06-04T09:00:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Juan Pablo Valencia Berrio',
      modulo: 'Seguridad',
      accion: 'Inicio de Sesión',
      detalle: 'Sesión administrativa iniciada correctamente para auditoría INVIMA.',
      ipAddress: '190.142.35.45'
    },
    {
      id: 'log-init-36',
      timestamp: '2026-06-04T10:00:00Z',
      companyId: 'company-insumedic',
      nombreEmpresa: 'Comercializadora Insumedic S.A.S. Zomac',
      usuario: 'Juan Pablo Valencia Berrio',
      modulo: 'Copias Seguridad',
      accion: 'Crear Copia',
      detalle: 'Copia de seguridad MANUAL generada con éxito (5 órdenes de fabricación, 4 actas de disposición de lotes).',
      ipAddress: '190.142.35.45'
    }
  ];
  localStorage.setItem('insumedic_activity_logs', JSON.stringify(defaultLogs));
  return defaultLogs;
}

export function saveLogs(logs: ActivityLog[]) {
  localStorage.setItem('insumedic_activity_logs', JSON.stringify(logs));
}

export function addAuditLog(
  usuario: string,
  modulo: string,
  accion: string,
  detalle: string,
  companyId?: string,
  nombreEmpresa?: string
) {
  const logs = getSavedLogs();
  const newLog: ActivityLog = {
    id: `log-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    timestamp: new Date().toISOString(),
    usuario,
    modulo,
    accion,
    detalle,
    companyId,
    nombreEmpresa,
    ipAddress: `${Math.floor(Math.random() * 100) + 100}.${Math.floor(Math.random() * 200)}.${Math.floor(Math.random() * 254)}.${Math.floor(Math.random() * 254)}`
  };
  const updated = [newLog, ...logs].slice(0, 50000); // Guardar historial completo sin recortar precozmente (límite subido a 50,000)
  saveLogs(updated);
  
  // En línea / Online direct Firestore synchronization
  uploadItemToFirestore('logs', newLog).catch(e => {
    console.warn('Failed to upload log to Firestore:', e);
  });
}

// Function to calculate remaining days in standard format
export function getDaysRemaining(expirationDateStr: string): number {
  const exp = new Date(expirationDateStr);
  const now = new Date();
  const diffTime = exp.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
}
